import { ReactNode, useCallback } from 'react';
import ImageProperties from '~/components/Properties/ImageProperties';
import PropertiesTooltip from '~/components/Properties/PropertiesTooltip';
import { sharesFrameOfReference } from '~/domain/dicom/uids';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import { ImageDetailsContext } from '~/viewportGrid';

/**
 * One pixel right of the title bar's left edge, so the card's border lies
 * beside the tile's frame line rather than on it.
 */
const CLEAR_OF_TILE_FRAME = {
    popperOptions: {
        modifiers: [{ name: 'offset', options: { offset: [1, 0] } }],
    },
};

/**
 * Hangs the details card of an image on the name a tile title gives it,
 * opening on hover and focus as the image rows of the sidebar do, flush
 * under the title bar. The
 * image comes from the registry entry that supplies the tile; a series the
 * registry does not hold keeps its bare name. A fused image on the known
 * frame of reference of the image it is fused over says so on its card.
 */
export function useTileImageDetails(
    registry: DatasetEnvironmentRegistry
): (context: ImageDetailsContext) => ReactNode {
    return useCallback(
        ({
            seriesInstanceUid,
            fusedOver,
            name,
            titleBar,
        }: ImageDetailsContext) => {
            const image = registry.entryFor(seriesInstanceUid)?.image;
            if (!image) {
                return name;
            }
            const base =
                fusedOver === undefined
                    ? undefined
                    : registry.entryFor(fusedOver)?.image;
            const sharesBaseFrame =
                base !== undefined &&
                sharesFrameOfReference(
                    base.frameOfReferenceUid,
                    image.frameOfReferenceUid
                );
            return (
                <PropertiesTooltip
                    placement="bottom-start"
                    anchorEl={titleBar}
                    PopperProps={CLEAR_OF_TILE_FRAME}
                    title={
                        <ImageProperties
                            image={image}
                            hasSharedFrameOfReference={sharesBaseFrame}
                        />
                    }
                >
                    {name}
                </PropertiesTooltip>
            );
        },
        [registry]
    );
}
